/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { useANCInterventions } from "./hooks/useANCInterventions";
import { ANCInterventionMatrix } from "./ANCInterventionMatrix";
import { ANCRecordInterventionDialog } from "./ANCRecordInterventionDialog";
import { history } from "../../../../Helpers/history";
import {
    TreatPageWrapper,
    TreatHeader,
    TreatHeaderTitle,
    TreatHeaderStats,
    StatBadge,
    EmptyState,
    GoToBookingBtn,
    LoadingWrapper,
} from "./ANCInterventionStyles";

/**
 * ANCInterventions — Page-level container for ANC Preventive Treatments Tracker
 *
 * Route: /gyn-and-obs-anc-preventive/:patient_number/:visit_id/:patient_type
 */
const ANCInterventions = function(props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    /* ─── Current user ─── */
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = (!_token.first_name ? "" : _token.first_name) + " " +
        (!_token.last_name ? "" : _token.last_name);

    /* ─── Interventions hook ─── */
    const iv = useANCInterventions(actions, patient_number, visit_id);

    /* ─── Snackbar ─── */
    const closeSnackbar = function() { actions.snackbarActions.hideSnackbar(); };

    /* ─── Navigate to ANC booking ─── */
    const goToBooking = function() {
        history.push(
            "/gyn-and-obs-antenatal-card/" + patient_number + "/" + visit_id + "/" + patient_type
        );
    };

    return (
        <div className="journals">
            <PageTitle title="ANC Preventive Treatments" />
            <SubHeader
                title="OB/GYN"
                subTitle="ANC Preventive Treatments Tracker"
                doctor_name={doctor_name}
            >
                <div className="header-icon-margin-right-zero">
                    <img
                        src="/images/antenatal-blue.png"
                        className="icon-resize surgery-icon-tabs ante-natal-green"
                        alt="ANC"
                    />
                </div>
            </SubHeader>
            <MainSnackbar
                variant={snackbar.type}
                open={snackbar.openBar}
                message={snackbar.message}
                handleCloseBar={closeSnackbar}
            />

            <ObGynSidebar
                patient_number={patient_number}
                visit_id={visit_id}
                patient_type={patient_type}
            >
                <TreatPageWrapper>
                    {/* ═══ PATIENT INFO ═══ */}
                    <ObGynPatientInfo
                        patient_number={patient_number}
                        visit_id={visit_id}
                    />

                    {/* ═══ LOADING STATE ═══ */}
                    {iv.loadingBooking && (
                        <LoadingWrapper>
                            <span>⏳</span> Loading patient ANC data...
                        </LoadingWrapper>
                    )}

                    {/* ═══ NO BOOKING STATE ═══ */}
                    {!iv.loadingBooking && !iv.bookingData && (
                        <div style={{ marginTop: "16px" }}>
                            <EmptyState>
                                <div className="empty-icon">💊</div>
                                <div className="empty-title">No Active ANC Booking Found</div>
                                <div className="empty-desc">
                                    This patient must be registered in the Antenatal Care program
                                    before preventive treatments can be tracked. The ANC booking links
                                    all interventions to the current pregnancy.
                                </div>
                                <GoToBookingBtn onClick={goToBooking}>
                                    Go to ANC Registration & Booking
                                </GoToBookingBtn>
                            </EmptyState>
                        </div>
                    )}

                    {/* ═══ MAIN CONTENT ═══ */}
                    {!iv.loadingBooking && iv.bookingData && (
                        <div style={{ marginTop: "16px" }}>

                            {/* ─── Matrix Tracker ─── */}
                            <ANCInterventionMatrix
                                displayMatrix={iv.displayMatrix}
                                summary={iv.summary}
                                gestationalAge={iv.gestationalAge}
                                showConditional={iv.showConditional}
                                setShowConditional={iv.setShowConditional}
                                openRecordDialog={iv.openRecordDialog}
                            />

                            {/* ─── Record Dialog ─── */}
                            <ANCRecordInterventionDialog
                                recordDialog={iv.recordDialog}
                                formData={iv.formData}
                                handleFormChange={iv.handleFormChange}
                                submitRecord={iv.submitRecord}
                                closeRecordDialog={iv.closeRecordDialog}
                                saving={iv.saving}
                            />
                        </div>
                    )}
                </TreatPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            snackbarActions: bindActionCreators(snackbarActions, dispatch),
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ANCInterventions);
